<template>
  <div id="workflowsTemplates">
    <card :title="$t('w-templates:filter')" :visible="false" collapsible>
      <div slot="card-body">
        <div class="form-row">
          <div class="form-group col-md-6">
            <label for="name" class="control-label">{{ $t('w-templates:name') | capitalize }}</label>
            <input id="name"
                   v-model="filterName"
                   v-validate="'text'"
                   :data-vv-as="$t('w-templates:name')"
                   :class="{ 'is-invalid': errors.has('name') }"
                   type="text"
                   name="name"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('name') }}</div>
          </div>

          <div class="form-group col-md-6">
            <label for="description" class="control-label">{{ $t('w-templates:description') | capitalize }}</label>
            <input id="description"
                   v-model="filterDescription"
                   v-validate="'text'"
                   :data-vv-as="$t('w-templates:description')"
                   :class="{ 'is-invalid': errors.has('description') }"
                   type="text"
                   name="description"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('description') }}</div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-md-6">
            <label for="category" class="control-label">{{ $t('w-templates:category') | capitalize }}</label>
            <select id="category" v-model="filterCategory" class="form-control" name="category">
              <option value="" />
              <option value="GATEWAY">{{ $t('w-templates:gateway') | capitalize }}</option>
              <option value="END_DEVICE">{{ $t('w-templates:endDevice') | capitalize }}</option>
            </select>
          </div>
        </div>
      </div>

      <div slot="card-footer">
        <button id="filterButton" class="btn btn-primary" @click="filter()">{{ $t('w-templates:search') | capitalize }}</button>
      </div>
    </card>

    <grid id="workflowTemplatesGrid"
          :title="$t('w-templates:workflows')"
          :columns="columns"
          :paginated-dto="templates"
          @load="filter"
    >
      <div slot="actions" slot-scope="props" :data-template-id="props.dto.id" class="text-right">
        <router-link v-if="props.dto.category==='GATEWAY'" to="/selection/gateways/workflow"><i class="icon-play" /></router-link>
        <router-link v-if="props.dto.category==='END_DEVICE'" to="/selection/end-devices/workflow"><i class="icon-play" /></router-link>
        <i v-show="isCompatible(props.dto)" class="icon-preview" @click="previewTemplate(props.dto)" />
        <i v-if="isRole.superAdmin" v-show="isCompatible(props.dto)" class="icon-edit" @click="editTemplate(props.dto)" />
        <i v-if="isRole.superAdmin" class="icon-trash" @click="confirmDeleteTemplate(props.dto)" />
      </div>
    </grid>

    <modal id="templatePreview"
           v-model="showPreviewModal"
           :title="previewModalTitle"
    >
      <div slot="modal-body">
        <div class="font-italic">{{ previewDescription | capitalize }}</div>

        <ul class="list-group mt-3">
          <li v-for="(webservice, index) in previewWebservices" :key="index" class="list-group-item d-flex justify-content-between align-items-center">
            <h5 class="mb-0">{{ $t('w-templates:task', {index: Number(index)+1}) | capitalize }} {{ webservice.name.variable }}</h5>
            <span v-if="webservice.wsType.variable==='asynchronous'" :title="$t('w-templates:asynchronous') | capitalize" class="icon-clock no-pointer" />
          </li>
        </ul>
      </div>
    </modal>

    <confirm v-if="isRole.superAdmin"
             id="deleteTemplate"
             v-model="showDeleteTemplateConfirm"
             :message="$t('w-templates:confirmDelete', {template: templateToDelete.name})"
             @confirm:validate="deleteTemplate"
    />

    <floating-button v-if="isRole.superAdmin" :title="$t('w-templates:create') | capitalize" :click-handler="createTemplate" icon-classes="icon-add" />
    <templates-form v-if="isRole.superAdmin" v-model="showTemplateForm" :template-id-to-edit="templateIdToEdit" @success="filter" @cancel="templateIdToEdit=''" />
  </div>
</template>

<script>
import { upperFirst } from 'lodash-es'
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'

const TemplatesForm = () => import(/* webpackChunkName: "v-workflows-form" */ './TemplatesForm')

export default {
  titleKey: 'workflowsTemplates',

  components: { TemplatesForm },

  data () {
    return {
      // filters
      filterName: null,
      filterDescription: null,
      filterCategory: null,
      // Grid
      templates: {},
      // Delete/update
      showDeleteTemplateConfirm: false,
      templateToDelete: {},
      templateIdToEdit: '',
      // Preview modal
      showPreviewModal: false,
      previewModalTitle: '',
      previewDescription: '',
      previewWebservices: {},
      // Add user
      showTemplateForm: false
    }
  },

  computed: {
    columns () {
      return [
        { text: this.$t('w-templates:name'), formatter: template => template.name, sort: 'name' },
        { text: this.$t('w-templates:description'), formatter: template => template.description },
        { text: this.$t('w-templates:category'), formatter: template => upperFirst(this.$t('w-templates:categories.' + template.category)) },
        { slot: 'actions' }
      ]
    }
  },

  created () {
    this.filter()
  },

  methods: {
    async filter (options = {}) {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      // Add filters in query params
      if (this.filterName) options.name = this.filterName
      if (this.filterDescription) options.description = this.filterDescription
      if (this.filterCategory) options.category = this.filterCategory

      // Get filtered data
      this.getTemplates(options)
    },

    isCompatible (template) {
      return template.category === 'GATEWAY' || template.category === 'END_DEVICE'
    },

    async getTemplates (options) {
      const params = this.$utils.getParams({ sort: '+name', name: '', description: '', category: '', fields: '!xmlB64', latest: true }, options, this.queryPageParams)
      let error, response
      [error, response] = await to(this.$api.getWorkflowTemplates(this.$store.getters.application, params))
      if (error || !response) return

      this.templates = response.data
    },

    createTemplate () {
      this.templateIdToEdit = ''
      this.showTemplateForm = true
    },

    previewTemplate (template) {
      this.showPreviewModal = true
      this.previewModalTitle = template.name
      this.previewDescription = template.description
      this.previewWebservices = template.processVariables
    },

    editTemplate (template) {
      this.templateIdToEdit = template.id
      this.showTemplateForm = true
    },

    confirmDeleteTemplate (template) {
      this.templateToDelete = template
      this.showDeleteTemplateConfirm = true
    },

    async deleteTemplate () {
      let error
      [error] = await to(this.$api.deleteWorkflowTemplate(this.templateToDelete))
      if (error) return

      this.filter()
      eventBus.$emit('message-success', this.$t('w-templates:removeSuccess', { template: this.templateToDelete.name }))
    }
  }
}
</script>
